// TelcoTrack — sdílená mřížka fotek (objekt „Připojené“ i nedodělky).

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';

export interface PhotoGridItem {
  url: string;
  id?: string;
  /** Objekt, pod kterým je fotka v DB (DELETE u agregované trasy). */
  objectId?: string;
  /** E-mail nahrávajícího; mazání, když není `deletable`. */
  uploadedBy?: string | null;
  /** Když je nastaveno, přepíše pravidlo autor/admin. */
  deletable?: boolean;
  /** Řádek pod fotkou. Objektová galerie ho nemá. */
  caption?: string | null;
}

@Component({
  selector: 'app-photo-grid',
  templateUrl: './photo-grid.component.html',
  styleUrls: ['./photo-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotoGridComponent {
  constructor(private readonly cdr: ChangeDetectorRef) {}

  @Input() photos: PhotoGridItem[] = [];
  @Input() showActions = true;
  @Input() currentUserEmail: string | null = null;
  @Input() isAdmin = false;
  @Input() deletingId: string | null = null;

  @Output() photoOpen = new EventEmitter<PhotoGridItem>();
  @Output() downloadPhoto = new EventEmitter<PhotoGridItem>();
  @Output() deletePhoto = new EventEmitter<PhotoGridItem>();

  /** 1×1 transparent GIF – po chybě načtení nespouštíme stále stejný HTTP GET. */
  private static readonly BROKEN_IMAGE_DATA_URL =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  private readonly failedPhotoKeys = new Set<string>();

  trackByPhotoId(index: number, photo: PhotoGridItem): string {
    return photo.id ?? `${index}:${photo.url}`;
  }

  photoSrc(item: PhotoGridItem): string {
    const k = item.id ?? item.url;
    return this.failedPhotoKeys.has(k) ? PhotoGridComponent.BROKEN_IMAGE_DATA_URL : item.url;
  }

  onImageError(event: Event, item: PhotoGridItem): void {
    const k = item.id ?? item.url;
    this.failedPhotoKeys.add(k);
    const target = event.target as HTMLImageElement;
    if (target) {
      target.onerror = null;
      target.removeAttribute('srcset');
      target.src = PhotoGridComponent.BROKEN_IMAGE_DATA_URL;
      target.style.opacity = '0.5';
    }
    this.cdr.markForCheck();
  }

  canDeleteItem(item: PhotoGridItem | null | undefined): boolean {
    if (!item?.id) return false;
    if (typeof item.deletable === 'boolean') return item.deletable;
    if (this.isAdmin) return true;
    return !!(this.currentUserEmail && item.uploadedBy === this.currentUserEmail);
  }

  stopNestedPointer(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Otevřít prohlížeč až po dokončení kliku, ať stejný klik nezavře nadřazený modal.
   */
  onPhotoClick(item: PhotoGridItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    setTimeout(() => this.photoOpen.emit(item), 0);
  }

  onDownload(item: PhotoGridItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.downloadPhoto.emit(item);
  }

  onDelete(item: PhotoGridItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (item.id) this.deletePhoto.emit(item);
  }
}
